import euList from '../assets/data/euList.json'
import { billedMonthsForDuration } from '@/utils/durationDiscount'
import {
  ALLPLATFORM_QUOTE_PLAN_IDENTIFIER,
  HOME_QUOTE_PLAN_IDENTIFIER,
  RELAY_DATA_ADDON_PLAN_IDENTIFIER,
  canAcceptRelayDataAddonPeriodResponse,
  createRelayDataAddonPeriodContext,
  defaultDataAddonQuantity,
  isDataAddonServiceEligible,
  parseDataAddonPolicies,
  parseRelayDataAddonPeriodResponse,
  quoteDataAddonSelection,
  resolveDataAddonOffer,
  selectDataAddonService,
  type DataAddonProductIdentifier,
  type DataAddonQuote,
  type ParsedDataAddonPolicies,
  type RelayDataAddonPeriodContext,
} from '@/utils/dataAddonPricing'
import { storeManagedPlan, type StoreManagement } from '@/utils/storeManaged'

const alreadyBoughtRequests = new WeakMap<object, { key: string, promise: Promise<void> }>()
const pricingConfigRequests = new WeakMap<object, { key: string, promise: Promise<void> }>()

function positiveUserId(value: unknown): number | null {
  const userId = Number(value)
  return Number.isInteger(userId) && userId > 0 ? userId : null
}

export type PricingMode = 'buyService' | 'topUp'

export interface CurrentServiceLike {
  id?: number
  plan_id?: number
  cate?: string
  status?: 'on' | 'off'
  quote?: number
  used?: number
  // 锚定按月计费字段（旧自然月服务为 null/缺失）
  billing_mode?: string | null
  period_start?: string | Date | null
  period_end?: string | Date | null
  anchor_day?: number | null
  extra?: {
    buy_length?: number
    buy_time?: string
    all_pre?: number
    data_addon_relay_period?: {
      period_key: string
      purchased_gb: number
    }
  }
}

export interface PurchaseType {
  type?: 'topUp' | 'new_buy' | 'extend' | 'upgrade' | 'downgrade' | 'corp_purchase' | 'pre' | 'allplatform_quote' | 'home_quote'
  alert?: boolean
  warning?: string
}

export interface FeeInput {
  mode: PricingMode
  selectedCate: string
  selectedTime: number
  selectedPlan: number | string
  extraPremium: number
  allPre: boolean | number | string
  currentAllPre: number
  maxPreForCurrentMonth: number
  showPromo: boolean
  promoCode: string
  purchaseType: PurchaseType
  selectedPaymentMethod?: { name?: string; currency?: string }
  topUpAmount: number
  currentService?: CurrentServiceLike
  promoCodeInfoRatio?: number | null
  anchorBillingScopeOverride?: string
  vatRateOverride?: number
  balanceOverride?: number
  dataAddonQuote?: DataAddonQuote
}

export interface FeeDetails {
  dueTimeAfterPurchase: string
  daysInMonth: number
  daysLeftThisMonth: number
  percentageLeftThisMonth: number
  upgradeProrationRatio: number | null
  realTime: number
  selectedPlanFee: number
  currentPlanFee: number
  currentMonthFee: number
  currentMonthFeeCalStr: string
  currentMonthFeeDisplay: number
  otherMonthFee: number
  otherMonthFeeCalStr: string
  vatReverseCharge: boolean
  vatRate: number
  exactLocalFeeBeforeTax: number
  exactVatAmount: number
  vatAmount: number
  exactLocalFee: number
  forcedMinimalFee: boolean
  roundUplocalFee: number
  localFeeDisplay: string
  balance: number
  exactAmountDue: number
  amountDue: number
  amountDueDisplay: string
  willPayWithRMB: boolean
  isYunshanfu: boolean
  canPayWithRMB: boolean
}

export const usePricing = defineStore('pricing', {
  state: () => ({
    cate: '' as string,
    plan: null as number | string | null,
    time: 12 as number, // 0 => premium data mode
    mode: 'buyService' as PricingMode,
    extraPremium: 10 as number,
    allPre: false as boolean,
    selectedPaymentMethod: {} as { name?: string; currency?: string },
    promoCode: '' as string,
    topUpAmount: 20 as number,
    alreadyBought: { allplatform: 0 } as Record<string, number>,
    dataAddonPlanId: null as number | null,
    alreadyBoughtPlanId: null as number | null,
    alreadyBoughtUserId: null as number | null,
    alreadyBoughtPeriodKey: null as string | null,
    alreadyBoughtSourceKey: null as string | null,
    alreadyBoughtStatus: 'idle' as 'idle' | 'loading' | 'ready' | 'error',
    prefilled: false as boolean,
    // 锚定按月计费 scope（读自 /api/client/pricing_config 的 anchor_billing_scope，
    // 已按本平台解析，与后端 buyPlan 实扣口径一致）。取值 off/new/expired/
    // new+expired/all。后端 all 覆盖所有套餐操作；本 store 只用它切换【新购/过期
    // 重购】预览（全新开通看 new，过期重购看 expired），存量服务展示仍按各自
    // billing_mode。
    anchorBillingScope: 'off' as string,
    dataAddonPolicies: { policies: {}, errors: {} } as ParsedDataAddonPolicies,
    dataAddonPricingStatus: 'idle' as 'idle' | 'loading' | 'ready' | 'error',
    _pricingConfigRequestId: 0 as number,
    _pricingConfigTargetKey: null as string | null,
    _alreadyBoughtRequestId: 0 as number,
  }),
  getters: {
    currentService(): CurrentServiceLike {
      const service = useUserService() as any
      if (this.time === 0 && Number.isInteger(this.dataAddonPlanId) && Number(this.dataAddonPlanId) > 0) {
        const exact = selectDataAddonService(service.serviceArray, {
          cate: this.cate,
          planId: Number(this.dataAddonPlanId),
          product: this.currentDataAddonProduct,
          fallbackProduct: this.currentDataAddonProduct === RELAY_DATA_ADDON_PLAN_IDENTIFIER
            ? ALLPLATFORM_QUOTE_PLAN_IDENTIFIER
            : undefined,
        })
        return exact || {}
      }
      return (service as any)[this.cate as string] || {}
    },
    // The store that owns the selected group's plan; data add-ons stay on sale.
    storeManagedPlan(): StoreManagement | null {
      return storeManagedPlan((useUserService() as any)[this.cate], { mode: this.mode, time: this.time })
    },
    currentAllPre(): number {
      return Number(this.currentService?.extra?.all_pre || 0)
    },
    currentRelayDataAddonPeriodContext(): RelayDataAddonPeriodContext | null {
      try {
        return createRelayDataAddonPeriodContext(this.currentService)
      } catch {
        return null
      }
    },
    alreadyBoughtReadyForCurrentPeriod(): boolean {
      const current = this.currentRelayDataAddonPeriodContext
      return Boolean(current
        && this.alreadyBoughtStatus === 'ready'
        && this.alreadyBoughtUserId === Number(useUser().id)
        && this.alreadyBoughtPlanId === current.planId
        && this.alreadyBoughtPeriodKey === current.periodKey
        && this.alreadyBoughtSourceKey === current.sourceKey)
    },
    maxPreForCurrentMonth(): number {
      const offer = this.currentDataAddonOffer
      if (!offer.ok || offer.kind !== 'per_gb') return 0
      if (offer.product !== RELAY_DATA_ADDON_PLAN_IDENTIFIER) return offer.maxPurchaseGb
      if (!this.alreadyBoughtReadyForCurrentPeriod) return 0
      return offer.maxPurchaseGb
    },
    currentDataAddonProduct(): DataAddonProductIdentifier {
      const selected = String(this.plan || '')
      if (selected === ALLPLATFORM_QUOTE_PLAN_IDENTIFIER) return ALLPLATFORM_QUOTE_PLAN_IDENTIFIER
      if (selected === HOME_QUOTE_PLAN_IDENTIFIER || this.cate === 'home') return HOME_QUOTE_PLAN_IDENTIFIER
      return RELAY_DATA_ADDON_PLAN_IDENTIFIER
    },
    currentDataAddonOffer() {
      return resolveDataAddonOffer(
        this.dataAddonPolicies,
        this.currentService?.plan_id,
        this.currentDataAddonProduct,
      )
    },
    dataAddonQuote(): DataAddonQuote {
      if (this.dataAddonPricingStatus !== 'ready') {
        return {
          ok: false,
          product: this.currentDataAddonProduct,
          planId: Number(this.currentService?.plan_id),
          reason: 'missing_policy',
          detail: this.dataAddonPricingStatus,
        }
      }
      if (!isDataAddonServiceEligible(this.currentService, this.currentDataAddonProduct)) {
        return {
          ok: false,
          product: this.currentDataAddonProduct,
          planId: Number(this.currentService?.plan_id),
          reason: 'missing_policy',
          detail: 'inactive_service',
        }
      }
      if (
        this.currentDataAddonProduct === RELAY_DATA_ADDON_PLAN_IDENTIFIER
        && !this.alreadyBoughtReadyForCurrentPeriod
      ) {
        return {
          ok: false,
          product: this.currentDataAddonProduct,
          planId: Number(this.currentService?.plan_id),
          reason: 'missing_policy',
          detail: `period_state_${this.alreadyBoughtStatus}`,
        }
      }
      return quoteDataAddonSelection({
        parsed: this.dataAddonPolicies,
        planId: this.currentService?.plan_id,
        product: this.currentDataAddonProduct,
        quantityGb: this.extraPremium,
        alreadyPurchasedGb: Number((this.alreadyBought as any).allplatform || 0),
        allPeriods: this.currentDataAddonProduct === RELAY_DATA_ADDON_PLAN_IDENTIFIER && Boolean(this.allPre),
        serviceBuyLength: Number(this.currentService?.extra?.buy_length || 0),
        alreadyFullPeriods: this.currentAllPre,
      })
    },
    balance(): number {
      const user = useUser()
      const $config = useRuntimeConfig().public as any
      if (!user.money) return 0
      if (user.balance_currency !== 'eur') {
        return (Number(user.money) || 0) / (Number($config.rmbToEurRate) || 1)
      }
      return Number(user.money) || 0
    },
    purchaseType(): PurchaseType {
      return computePurchaseType({
        mode: this.mode,
        selectedCate: this.cate,
        selectedTime: this.time,
        selectedPlan: this.plan,
        currentService: this.currentService,
      })
    },
  },
  actions: {
    setState(partial: Partial<ReturnType<(typeof this)['$state']>>) {
      Object.assign(this.$state, partial)
      this.prefilled = true
    },
    fromQuery(q: Record<string, any>) {
      const firstValue = (value: any) => (Array.isArray(value) ? value[0] : value)
      const cateRaw = firstValue(q.cate)
      const planRawValue = firstValue(q.plan)
      const timeRawValue = firstValue(q.time)
      const modeRawValue = firstValue(q.mode)
      const promoRawValue = firstValue(q.promo ?? q.promoCode)
      const amountRawValue = firstValue(q.amount)
      const payRawValue = firstValue(q.pay ?? q.payment ?? q.method)
      const extraPremiumRawValue = firstValue(q.extraPremium ?? q.extra_premium ?? q.pre)
      const allPreRawValue = firstValue(q.allPre ?? q.all_pre)
      const addonPlanIdRawValue = firstValue(q.addonPlanId ?? q.addon_plan_id)

      const cate = cateRaw ? String(cateRaw) : ''
      const planString = planRawValue != null ? String(planRawValue) : ''
      const planRaw = planRawValue != null && !Number.isNaN(Number(planRawValue))
        ? Number(planRawValue)
        : ([RELAY_DATA_ADDON_PLAN_IDENTIFIER, ALLPLATFORM_QUOTE_PLAN_IDENTIFIER, HOME_QUOTE_PLAN_IDENTIFIER].includes(planString as DataAddonProductIdentifier) ? planString : null)
      const timeRaw = timeRawValue != null && !Number.isNaN(Number(timeRawValue)) ? Number(timeRawValue) : null
      const modeRaw = modeRawValue ? String(modeRawValue) : ''
      const promoRaw = promoRawValue ? String(promoRawValue) : ''
      const amountRaw = amountRawValue != null && !Number.isNaN(Number(amountRawValue)) ? Number(amountRawValue) : null
      const extraPremiumRaw =
        extraPremiumRawValue != null && !Number.isNaN(Number(extraPremiumRawValue)) ? Number(extraPremiumRawValue) : null
      const allPreRaw = allPreRawValue != null ? String(allPreRawValue).toLowerCase() : ''
      const addonPlanIdRaw = addonPlanIdRawValue != null && /^[1-9]\d*$/.test(String(addonPlanIdRawValue))
        ? Number(addonPlanIdRawValue)
        : null
      const payRaw = payRawValue ? String(payRawValue) : ''
      const dataAddonPlan = [RELAY_DATA_ADDON_PLAN_IDENTIFIER, ALLPLATFORM_QUOTE_PLAN_IDENTIFIER, HOME_QUOTE_PLAN_IDENTIFIER]
        .includes(planString as DataAddonProductIdentifier)

      if (cate) this.cate = cate
      if (planRaw != null) this.plan = planRaw
      if (timeRaw != null) {
        this.time = timeRaw
      }
      if (dataAddonPlan) {
        this.cate = planString === HOME_QUOTE_PLAN_IDENTIFIER ? 'home' : 'allplatform'
        this.time = 0
        this.mode = 'buyService'
        this.dataAddonPlanId = addonPlanIdRaw
      }
      if (modeRaw === 'topUp' || modeRaw === 'buyService') this.mode = modeRaw as PricingMode
      if (promoRaw) this.promoCode = promoRaw
      if (amountRaw != null) this.topUpAmount = amountRaw
      if (extraPremiumRaw != null) this.extraPremium = extraPremiumRaw
      if (allPreRaw) {
        this.allPre = ['1', 'true', 'yes', 'on'].includes(allPreRaw) || Number(allPreRaw) > 0
      }
      if (payRaw) {
        this.selectedPaymentMethod = {
          ...(this.selectedPaymentMethod || {}),
          name: payRaw,
        }
      }

      if (this.cate && this.plan == null) {
        this.plan = this.cate === 'home' ? 6 : 4
      }
      if (this.time === 0) {
        if (this.cate === 'home') {
          this.plan = HOME_QUOTE_PLAN_IDENTIFIER
          this.allPre = false
        } else if (![RELAY_DATA_ADDON_PLAN_IDENTIFIER, ALLPLATFORM_QUOTE_PLAN_IDENTIFIER].includes(String(this.plan) as DataAddonProductIdentifier)) {
          this.plan = RELAY_DATA_ADDON_PLAN_IDENTIFIER
        }
        if (extraPremiumRaw == null) this.setDefaultDataAddonQuantity()
      }

      this.prefilled = true
    },
    clearPrefill() {
      this.prefilled = false
    },
    selectDataAddonProduct(product: DataAddonProductIdentifier) {
      const service = useUserService() as any
      const selectedServiceExists = service.serviceArray?.some?.((entry: CurrentServiceLike | null) =>
        entry?.cate === this.cate && Number(entry?.plan_id) === Number(this.dataAddonPlanId))
      if (!selectedServiceExists) {
        const defaultService = (service as any)[this.cate as string]
        const planId = Number(defaultService?.plan_id)
        this.dataAddonPlanId = Number.isInteger(planId) && planId > 0 ? planId : null
      }
      this.plan = product
      this.time = 0
      if (product !== RELAY_DATA_ADDON_PLAN_IDENTIFIER) this.allPre = false
      this.setDefaultDataAddonQuantity()
    },
    setDefaultDataAddonQuantity() {
      const quantity = defaultDataAddonQuantity(this.currentDataAddonOffer)
      if (quantity != null) this.extraPremium = quantity
    },
    async loadAlreadyBought() {
      const planId = Number(this.currentService?.plan_id)
      if (!Number.isInteger(planId) || ![2, 4].includes(planId)) {
        this._alreadyBoughtRequestId += 1
        alreadyBoughtRequests.delete(this)
        this.alreadyBought = { allplatform: 0 }
        this.alreadyBoughtPlanId = null
        this.alreadyBoughtUserId = null
        this.alreadyBoughtPeriodKey = null
        this.alreadyBoughtSourceKey = null
        this.alreadyBoughtStatus = 'idle'
        return
      }
      let expected: RelayDataAddonPeriodContext
      try {
        expected = createRelayDataAddonPeriodContext(this.currentService)
      } catch {
        this._alreadyBoughtRequestId += 1
        alreadyBoughtRequests.delete(this)
        this.alreadyBought = { allplatform: 0 }
        this.alreadyBoughtPlanId = null
        this.alreadyBoughtUserId = null
        this.alreadyBoughtPeriodKey = null
        this.alreadyBoughtSourceKey = null
        this.alreadyBoughtStatus = 'error'
        return
      }
      const userId = Number(useUser().id)
      const requestKey = JSON.stringify([userId, expected.planId, expected.periodKey, expected.sourceKey])
      if (this.alreadyBoughtReadyForCurrentPeriod) return
      const activeRequest = alreadyBoughtRequests.get(this)
      if (activeRequest?.key === requestKey) return activeRequest.promise

      const requestId = ++this._alreadyBoughtRequestId
      this.alreadyBought = { allplatform: 0 }
      this.alreadyBoughtPlanId = null
      this.alreadyBoughtUserId = null
      this.alreadyBoughtPeriodKey = null
      this.alreadyBoughtSourceKey = null
      this.alreadyBoughtStatus = 'loading'
      const request = (async () => {
        try {
          const service = useUserService()
          const data = await service.getAlreadyBoughtPremiumData(planId)
          const current = this.currentRelayDataAddonPeriodContext
          if (
            Number(useUser().id) !== userId
            || !canAcceptRelayDataAddonPeriodResponse(
              requestId,
              this._alreadyBoughtRequestId,
              expected,
              current,
            )
          ) {
            if (requestId === this._alreadyBoughtRequestId) this.alreadyBoughtStatus = 'error'
            return
          }
          const parsed = parseRelayDataAddonPeriodResponse(data, expected)
          this.alreadyBought = { allplatform: parsed.purchasedGb }
          this.alreadyBoughtPlanId = parsed.planId
          this.alreadyBoughtUserId = userId
          this.alreadyBoughtPeriodKey = parsed.periodKey
          this.alreadyBoughtSourceKey = expected.sourceKey
          this.alreadyBoughtStatus = 'ready'
        } catch {
          const current = this.currentRelayDataAddonPeriodContext
          if (
            Number(useUser().id) !== userId
            || !canAcceptRelayDataAddonPeriodResponse(
              requestId,
              this._alreadyBoughtRequestId,
              expected,
              current,
            )
          ) return
          this.alreadyBought = { allplatform: 0 }
          this.alreadyBoughtPlanId = null
          this.alreadyBoughtUserId = null
          this.alreadyBoughtPeriodKey = null
          this.alreadyBoughtSourceKey = null
          this.alreadyBoughtStatus = 'error'
        }
      })()
      alreadyBoughtRequests.set(this, { key: requestKey, promise: request })
      try {
        await request
      } finally {
        if (alreadyBoughtRequests.get(this)?.promise === request) {
          alreadyBoughtRequests.delete(this)
        }
      }
    },
    invalidateAlreadyBought() {
      this._alreadyBoughtRequestId += 1
      alreadyBoughtRequests.delete(this)
      this.alreadyBought = { allplatform: 0 }
      this.alreadyBoughtPlanId = null
      this.alreadyBoughtUserId = null
      this.alreadyBoughtPeriodKey = null
      this.alreadyBoughtSourceKey = null
      this.alreadyBoughtStatus = 'idle'
    },
    // Cache one complete pricing snapshot for the current target during this SPA session.
    // A target change or explicit invalidation fails closed and starts a fresh request.
    async loadPricingConfig(asUser?: number) {
      const session = useVisitorSession()
      const requestedTargetUserId = positiveUserId(asUser)
      const requestTargetUserId = session.isAdmin ? requestedTargetUserId : null
      const selfUserId = positiveUserId(session.user_id) ?? positiveUserId(useUser().id)
      const targetUserId = requestTargetUserId ?? selfUserId
      const targetKey = targetUserId == null ? 'anonymous' : `user:${targetUserId}`
      if (this.dataAddonPricingStatus === 'ready' && this._pricingConfigTargetKey === targetKey) return
      const activeRequest = pricingConfigRequests.get(this)
      if (activeRequest?.key === targetKey) return activeRequest.promise

      const requestId = ++this._pricingConfigRequestId
      if (this._pricingConfigTargetKey !== targetKey) {
        this.anchorBillingScope = 'off'
        this.dataAddonPolicies = { policies: {}, errors: {} }
      }
      this._pricingConfigTargetKey = targetKey
      this.dataAddonPricingStatus = 'loading'
      const request = (async () => {
        try {
          const data = (await useAPI().get('client/pricing_config', {
            params: requestTargetUserId == null ? {} : { asUser: requestTargetUserId },
          })) as {
            anchor_billing_scope?: string
            data_addon_policies?: unknown
          }
          if (requestId !== this._pricingConfigRequestId) return
          // 新契约：anchor_billing_scope 是已按本平台解析的 enum。缺失（旧后端）时
          // 回退 'off'（自然月），fail-safe。
          this.anchorBillingScope = String(data?.anchor_billing_scope || 'off')
          this.dataAddonPolicies = parseDataAddonPolicies(data?.data_addon_policies)
          this.dataAddonPricingStatus = Object.keys(this.dataAddonPolicies.errors).length === 0 ? 'ready' : 'error'
          if (this.time === 0 && !this.dataAddonQuote.ok) this.setDefaultDataAddonQuantity()
        } catch {
          if (requestId !== this._pricingConfigRequestId) return
          this.anchorBillingScope = 'off'
          this.dataAddonPolicies = { policies: {}, errors: {} }
          this.dataAddonPricingStatus = 'error'
        }
      })()
      pricingConfigRequests.set(this, { key: targetKey, promise: request })
      try {
        await request
      } finally {
        if (pricingConfigRequests.get(this)?.promise === request) {
          pricingConfigRequests.delete(this)
        }
      }
    },
    invalidatePricingConfig() {
      this._pricingConfigRequestId += 1
      pricingConfigRequests.delete(this)
      this._pricingConfigTargetKey = null
      this.anchorBillingScope = 'off'
      this.dataAddonPolicies = { policies: {}, errors: {} }
      this.dataAddonPricingStatus = 'idle'
    },
    calculateFeeDetails(override: Partial<FeeInput> = {}): FeeDetails {
      const selectedPlanDefault = this.cate === 'home' ? 6 : 4
      const input: FeeInput = {
        mode: this.mode,
        selectedCate: this.cate,
        selectedTime: this.time,
        selectedPlan: this.plan ?? selectedPlanDefault,
        extraPremium: this.extraPremium,
        allPre: this.allPre,
        currentAllPre: this.currentAllPre,
        maxPreForCurrentMonth: this.maxPreForCurrentMonth,
        showPromo: Boolean(this.promoCode),
        promoCode: this.promoCode,
        purchaseType: this.purchaseType,
        selectedPaymentMethod: this.selectedPaymentMethod || undefined,
        topUpAmount: this.topUpAmount,
        currentService: this.currentService,
        dataAddonQuote: this.dataAddonQuote,
        ...override,
      }
      return computeFeeDetails(input)
    },
  },
})

// 这里只判定前端 new_buy 预览是否按整月显示：无存量服务 = 后端 newBuy
// （scope∈{new,new+expired,all}）；有存量(off/过期)服务重购 = 后端 expired
// （scope∈{expired,new+expired,all}）。后端的 all 还覆盖 up/down/rebuy/extend；
// 前端 extend 对应后端 rebuy。active legacy 的这些操作仍按原 legacy 规则计算
// 本次金额和到期，成功后才以当前自然月为桥接周期转 Anchor，因此不由本 helper
// 改写预览公式。
export function anchorScopeAppliesToNewBuy(
  scope: string | undefined,
  currentService: CurrentServiceLike | undefined,
): boolean {
  const isFresh = !currentService?.plan_id
  if (isFresh) {
    return scope === 'new' || scope === 'new+expired' || scope === 'all'
  }
  return scope === 'expired' || scope === 'new+expired' || scope === 'all'
}

export function resolveUpgradeProrationRatio(
  dayjs: any,
  currentService: CurrentServiceLike,
  percentageLeftThisMonth: number,
  anchorBillingScope: string | undefined,
): number {
  if (currentService.plan_id === 1) {
    return anchorBillingScope === 'all' ? 1 : percentageLeftThisMonth
  }
  if (isAnchorBilledService(currentService)) {
    return anchoredPeriodRemainingFraction(currentService)
  }
  const buyTime = dayjs(currentService.extra?.buy_time).tz('Asia/Shanghai')
  const buyTimeReference = buyTime && typeof buyTime.isValid === 'function' && buyTime.isValid()
    ? buyTime
    : dayjs().tz('Asia/Shanghai')
  const totalDays = buyTimeReference.daysInMonth()
  return (totalDays - buyTimeReference.date() + 1) / totalDays
}

export function computeFeeDetails(props: FeeInput): FeeDetails {
  const dayjs = useDayjs()
  const user = useUser()
  const service = useUserService()
  const $config = useRuntimeConfig().public as any

  const moneyShape = (input: number) => Math.ceil(input * 100) / 100
  const isEu = (countryName?: string) => (countryName ? (euList as any).includes(countryName) : false)
  const getDaysInMonth = (dayjsObj: any) => new Date(dayjsObj.year(), dayjsObj.month() + 1, 0).getDate()
  const toCurrencyString = (amount: number, currency = 'EUR') => {
    try {
      // Use runtime default locale; avoids vue-i18n `useI18n()` restrictions.
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount || 0)
    } catch {
      const signMapping: Record<string, string> = { CNY: '¥', EUR: '€', USD: '$' }
      return `${signMapping[String(currency).toUpperCase()] || ''} ${Number(amount || 0).toFixed(2)}`
    }
  }

  const currentService: CurrentServiceLike = props.currentService || (service as any)[props.selectedCate as string] || {}
  const feeList = $config.feeList

  const balance = (() => {
    if (props.balanceOverride != null) {
      if (!Number.isFinite(props.balanceOverride) || props.balanceOverride < 0) {
        throw new Error(`Invalid balance override: ${props.balanceOverride}`)
      }
      return props.balanceOverride
    }
    if (!user.money) return 0
    if (user.balance_currency !== 'eur') return user.money / $config.rmbToEurRate
    return user.money
  })()

  const dataAddonMode = ['allplatform', 'home'].includes(props.selectedCate) && props.selectedTime === 0
  const currentPlanId = currentService?.plan_id
  const anchorBillingScope = props.anchorBillingScopeOverride ?? usePricing().anchorBillingScope

  // 该局部值只控制 new_buy 是否显示整月预览。upgrade/downgrade/extend 不在这里
  // 切换公式：scope=all 命中 active legacy 时，本次仍按 legacy 算，成功后才桥接
  // 转 Anchor；前端 extend 对应后端 rebuy。
  const anchorNewBuy =
    props.purchaseType.type === 'new_buy' &&
    anchorScopeAppliesToNewBuy(
      anchorBillingScope,
      currentService,
    )

  const daysInMonth = getDaysInMonth(dayjs())
  const daysLeftThisMonth = daysInMonth - dayjs().tz('Asia/Shanghai').date() + 1
  // ON：新购以购买日为锚点开整周期，整月计价（后端 feePercent=1）。
  // OFF：旧的自然月按天折算（后端 feePercent=daysLeftInMonthPercent()）。
  const percentageLeftThisMonth = anchorNewBuy
    ? 1
    : daysInMonth
      ? daysLeftThisMonth / daysInMonth
      : 0
  const upgradeProrationRatio = props.purchaseType.type === 'upgrade'
    ? resolveUpgradeProrationRatio(dayjs, currentService, percentageLeftThisMonth, anchorBillingScope)
    : null

  const selectedPlanId = Number(props.selectedPlan)
  const selectedPlanFee = Number.isFinite(selectedPlanId) ? feeList[selectedPlanId] : 0
  const currentPlanFee = feeList[currentPlanId as number]

  const realTime = dataAddonMode ? 0 : billedMonthsForDuration(props.selectedTime)

  const currentMonthFee = selectedPlanFee * percentageLeftThisMonth
  const otherMonthFee = ['new_buy'].includes(props.purchaseType.type as string) ? (realTime - 1) * selectedPlanFee : 0
  const currentMonthFeeCalStr = anchorNewBuy
    ? `Full month fee: ${selectedPlanFee} x 1 month (anchored billing)`
    : `Full month fee: ${selectedPlanFee} x ${(percentageLeftThisMonth * 100).toFixed(2)}% (rounded up)`
  const otherMonthFeeCalStr = ['new_buy'].includes(props.purchaseType.type as string)
    ? `${selectedPlanFee} (full month fee) x ${realTime - 1} (left month)`
    : ''

  if (
    props.vatRateOverride != null &&
    (!Number.isFinite(props.vatRateOverride) || props.vatRateOverride < 0)
  ) {
    throw new Error(`Invalid VAT rate override: ${props.vatRateOverride}`)
  }
  const vatReverseCharge =
    props.vatRateOverride == null &&
    Boolean(user.billing?.company_vat && user.billing?.country !== 'Netherlands')
  const vatRate = (() => {
    if (props.vatRateOverride != null) return props.vatRateOverride
    let rate = 0
    if (isEu(user.billing?.country)) {
      rate = 0.21
      if (vatReverseCharge) rate = 0
    }
    return rate
  })()

  const promoRatio = props.promoCodeInfoRatio ?? 1

  function calcTopupFee(): number {
    const ret = Number(props.topUpAmount)
    if (ret < 1) return 1
    return ret
  }

  function calcBuyServiceFee(): number {
    if (dataAddonMode) {
      if (!props.dataAddonQuote?.ok) {
        throw new Error(`Data add-on pricing is unavailable: ${props.dataAddonQuote?.reason || 'missing_quote'}`)
      }
      return moneyShape(props.dataAddonQuote.subtotalCents / 100)
    }

    if (props.purchaseType.type === 'downgrade') return 0

    if (props.purchaseType.type === 'upgrade') {
      if (upgradeProrationRatio == null) {
        throw new Error('Missing upgrade proration ratio')
      }
      const diff = selectedPlanFee - currentPlanFee
      let monthCount = Number(currentService?.extra?.buy_length || 0)
      if ((currentService?.plan_id ?? 0) === 1) {
        monthCount = 1
      }
      if (!Number.isFinite(monthCount) || monthCount <= 0) monthCount = 1
      monthCount = billedMonthsForDuration(monthCount)
      const currentMonth = diff * upgradeProrationRatio
      const otherMonth = diff * (monthCount - 1)
      const ret = (currentMonth + otherMonth) * (promoRatio || 1)
      return ret
    }

    if (props.purchaseType.type === 'extend') {
      return selectedPlanFee * realTime * (promoRatio || 1)
    }

    return (currentMonthFee + otherMonthFee) * (promoRatio || 1)
  }

  const exactLocalFeeBeforeTax = props.mode === 'buyService' ? calcBuyServiceFee() : calcTopupFee()
  const exactVatAmount = exactLocalFeeBeforeTax * vatRate
  const vatAmount = Math.ceil(exactVatAmount * 100) / 100
  const exactLocalFee = Math.ceil((exactLocalFeeBeforeTax + exactVatAmount) * 100) / 100

  const forcedMinimalFee = exactLocalFee > 0 && exactLocalFee < 1
  const roundUplocalFee = forcedMinimalFee ? 1 : exactLocalFee
  const localFeeDisplay = toCurrencyString(exactLocalFee, feeList.currencyCode)

  const exactAmountDue = props.mode === 'topUp' ? exactLocalFee : exactLocalFee <= balance ? 0 : exactLocalFee - balance

  const amountDue = exactAmountDue > 0 && exactAmountDue < 1 ? 1 : exactAmountDue
  const amountDueDisplay = toCurrencyString(amountDue, feeList.currencyCode)

  const willPayWithRMB = Boolean(
    props.selectedPaymentMethod?.name?.endsWith('alipay') || props.selectedPaymentMethod?.name?.endsWith('wechat'),
  )
  const isYunshanfu = props.selectedPaymentMethod?.name === 'paymentwall'
  const canPayWithRMB = Boolean(
    props.selectedPaymentMethod?.name?.startsWith('paypal') || props.selectedPaymentMethod?.name === 'paymentwall',
  )

  const now = dayjs().tz('Asia/Shanghai')
  const dueTimeAfterPurchase = (() => {
    const btRaw = currentService?.extra?.buy_time
    const bl = Number(currentService?.extra?.buy_length || 0)
    const bt = btRaw ? dayjs(btRaw).tz('Asia/Shanghai') : null

    // 锚定服务（存量按行判定，与开关无关）：到期日 = period_end + 剩余预付月数
    if (isAnchorBilledService(currentService)) {
      const expiry = anchoredServiceExpiry(dayjs, currentService)
      if (['downgrade', 'upgrade'].includes(props.purchaseType.type as string)) {
        return expiry.format(ANCHOR_DUE_FORMAT)
      }
      if (props.purchaseType.type === 'extend') {
        const anchorDay = currentService.anchor_day || expiry.date()
        return addAnchoredMonths(dayjs, expiry.toDate(), Number(props.selectedTime || 0), anchorDay).format(
          ANCHOR_DUE_FORMAT,
        )
      }
    }
    // 新购（含旧服务过期后重购）+ 锚定开：从今天锚定，selectedTime 个月后到期。
    // 开关关时落到下方 legacy「次月1日」分支。
    if (anchorNewBuy && props.purchaseType.type === 'new_buy') {
      return addAnchoredMonths(dayjs, now.toDate(), Number(props.selectedTime || 0), now.date()).format(
        ANCHOR_DUE_FORMAT,
      )
    }

    if (['downgrade', 'upgrade'].includes(props.purchaseType.type as string)) {
      if (bt && bt.isValid()) {
        return bt.set('date', 1).add(bl, 'month').set('date', 1).format('YYYY/MM/01')
      }
      return now.startOf('month').add(1, 'month').format('YYYY/MM/01')
    }

    if (props.purchaseType.type === 'extend') {
      if (bt && bt.isValid()) {
        return bt
          .set('date', 1)
          .add(bl + Number(props.selectedTime || 0), 'month')
          .set('date', 1)
          .format('YYYY/MM/01')
      }
      return now
        .startOf('month')
        .add(Number(props.selectedTime || 0), 'month')
        .format('YYYY/MM/01')
    }

    return now
      .startOf('month')
      .add(Number(props.selectedTime || 0), 'month')
      .format('YYYY/MM/01')
  })()

  const sumAfterCeil = moneyShape(currentMonthFee) + moneyShape(otherMonthFee)
  const currentMonthFeeDisplay = sumAfterCeil > roundUplocalFee ? currentMonthFee : moneyShape(currentMonthFee)

  return {
    dueTimeAfterPurchase,
    daysInMonth,
    daysLeftThisMonth,
    percentageLeftThisMonth,
    upgradeProrationRatio,
    realTime,
    selectedPlanFee,
    currentPlanFee,
    currentMonthFee,
    currentMonthFeeCalStr,
    currentMonthFeeDisplay,
    otherMonthFee,
    otherMonthFeeCalStr,
    vatReverseCharge,
    vatRate,
    exactLocalFeeBeforeTax,
    exactVatAmount,
    vatAmount,
    exactLocalFee,
    forcedMinimalFee,
    roundUplocalFee,
    localFeeDisplay,
    balance,
    exactAmountDue,
    amountDue,
    amountDueDisplay,
    willPayWithRMB,
    isYunshanfu,
    canPayWithRMB,
  }
}

export function computePurchaseType(input: {
  mode: PricingMode
  selectedCate: string
  selectedTime: number
  selectedPlan: number | string | null
  currentService?: CurrentServiceLike
}): PurchaseType {
  const service = useUserService() as any
  const currentService = input.currentService || (service as any)[input.selectedCate as string] || {}
  const extraPremiumDataMode = input.selectedCate === 'allplatform' && input.selectedTime === 0
  const homeQuoteDataMode = input.selectedCate === 'home' && input.selectedTime === 0
  if (input.mode === 'topUp') return { type: 'topUp' }
  if (extraPremiumDataMode) {
    return {
      type: input.selectedPlan === ALLPLATFORM_QUOTE_PLAN_IDENTIFIER
        ? ALLPLATFORM_QUOTE_PLAN_IDENTIFIER
        : RELAY_DATA_ADDON_PLAN_IDENTIFIER,
    }
  }
  if (homeQuoteDataMode) return { type: 'home_quote' }
  const currentPlanId = currentService?.plan_id
  const newBuyResult = (): PurchaseType => ({ type: 'new_buy', alert: false, warning: '' })
  if (!currentPlanId) {
    return newBuyResult()
  }
  if (
    currentService?.status === 'off' &&
    isAnchorBilledService(currentService) &&
    +new Date(currentService?.period_end as any) <= Date.now()
  ) {
    return newBuyResult()
  }
  // 锚定服务：on 但当前周期已过且仅剩最后一个预付月（daemon 尚未续期）——
  // 后端把此情形归类为 expired（整月重锚定），前端须报 new_buy，否则会误算成 extend 的价格/日期
  if (
    currentService?.status === 'on' &&
    currentService?.extra?.buy_length === 1 &&
    isAnchorBilledService(currentService) &&
    +new Date(currentService?.period_end as any) <= Date.now()
  ) {
    return newBuyResult()
  }
  if (currentService?.status === 'off') {
    if (currentService?.extra?.buy_length === 1) {
      return newBuyResult()
    } else if ((currentPlanId as any) === (input.selectedPlan as any)) {
      const canRestoreWithData = isDataAddonServiceEligible(
        currentService,
        input.selectedCate === 'home' ? HOME_QUOTE_PLAN_IDENTIFIER : ALLPLATFORM_QUOTE_PLAN_IDENTIFIER,
      )
      return {
        type: 'extend',
        warning: canRestoreWithData ? 'please_add_data_not_extend'
          : (input.selectedPlan as any) === 6 ? 'contact_cs_for_options' : 'please_upgrade_not_extend',
        alert: canRestoreWithData || (input.selectedPlan as any) !== 6,
      }
    } else if ((currentPlanId as any) < (input.selectedPlan as any)) {
      return { type: 'upgrade', warning: 'will_upgrade_all' }
    } else {
      return { type: 'downgrade', warning: 'downgrade', alert: true }
    }
  }
  if ((currentPlanId as any) === (input.selectedPlan as any)) return { type: 'extend' }
  if ((currentPlanId as any) < (input.selectedPlan as any)) return { type: 'upgrade', warning: 'will_upgrade_all' }
  return { type: 'downgrade', warning: 'downgrade', alert: true }
}
